import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft2,
  ArrowRight2,
  Cross,
} from "../CustomComponents/SwiftIcon/Icons";
import { numberFormat, priceFormat } from "../../exports/UtilFunctions";
import MFSearch from "./MFSearch";
import ServerRequest from "../../exports/ServerRequest";
import {
  generatePurchaseDropdownOptions,
  generateRedemptionDropdownOptions,
} from "../../exports/generatePurchaseDropdownOptions";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomSelectPortal from "../Body/CustomChartComponents/CustomSelect/CustomSelectPortal";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import ConfirmationBox from "./ConfirmationBox";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import Pulse from "../Loader/Pulse";
import successIcon from "../../assets/icons/success.svg";
import failIcon from "../../assets/icons/fail.svg";
import ConfirmationTable from "./ConfirmationTable";

const DEVICE_SIZE = getDeviceSize();

const MFSell = ({ popupData, portfolioData = [], setPopup, customer }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Fetching your order");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [transferData, setTransferData] = useState([]);
  const [totalCurrentValue, setTotalCurrentValue] = useState(0);
  const [totalTransactionValue, setTotalTransactionValue] = useState(0);
  const [totalPostTransactionValue, setTotalPostTransactionValue] = useState(0);
  const [currentStep, setCurrentStep] = useState("Process");
  const [clientAccounts, setClientAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [successData, setSuccessData] = useState([]);
  const [failedData, setFailedData] = useState([]);
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmationType, setConfirmationType] = useState("");

  useEffect(() => {
    const loadData = async () => {
      if (!popupData) return;
      setLoading(true);

      const newItems = popupData.isSingle
        ? [{ ...popupData, source: "popup" }]
        : Array.isArray(popupData)
          ? popupData.map((item) => ({ ...item, source: "popup" }))
          : [];

      setTransferData(newItems);

      // Wait for both NAV and sell info fetches
      await Promise.all(
        newItems.map(async (item) => {
          const isin = item.isin || item.ISIN;
          await Promise.all([fetchSellInfo(isin), fetchCurrentNAV(isin)]);
        }),
      );

      setLoading(false);
    };

    loadData();
  }, [popupData]);

  useEffect(() => {
    if (!loading && !confirmationLoading) return;

    const loadingMessages = loading
      ? ["Loading mutual fund data...", "Almost there..."]
      : [
          "Processing redemption order...",
          "Loading...",
          "Finalizing transaction...",
        ];

    let index = 0;
    setLoadingMsg(loadingMessages[index]);
    setOpacityMsg(true);

    const loadingMessageInterval = setInterval(() => {
      setOpacityMsg(false);
      setTimeout(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingMsg(loadingMessages[index]);
        setOpacityMsg(true);
      }, 200);
    }, 3000);

    return () => clearInterval(loadingMessageInterval);
  }, [loading, confirmationLoading]);

  useEffect(() => {
    let investmentValue = 0;
    let transaction = 0;
    let postTransaction = 0;

    transferData.forEach((val) => {
      investmentValue += Number(val.investment_value) || 0;
      transaction += Number(val.selectedAmount) || 0;
      postTransaction += Number(val.post_transaction) || 0;
    });

    setTotalCurrentValue(investmentValue.toFixed(2));
    setTotalTransactionValue(transaction);
    setTotalPostTransactionValue(postTransaction);
  }, [transferData]);

  const fetchSellInfo = async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/redemption/${isin}`,
        method: "GET",
      });

      if (res) {
        setTransferData((prev) =>
          prev.map((item) =>
            (item.isin || item.ISIN) === isin
              ? { ...item, sellInfo: res.data }
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching purchase info for ISIN:", isin, error);
    }
  };

  const fetchCurrentNAV = async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/latestnav/${isin}`,
        method: "get",
      });
      const nav = res?.result?.latest_nav;

      if (res) {
        setTransferData((prev) =>
          prev.map((item) =>
            (item.isin || item.ISIN) === isin
              ? { ...item, current_nav: nav }
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching current NAV for ISIN:", isin, error);
    }
  };

  const handleProcess = async () => {
    try {
      const res = await ServerRequest({
        url: `/nse/get-client-account?account_code=${customer.account_code}`,
        method: "GET",
      });

      if (res?.data?.report_data?.length) {
        setClientAccounts(res.data.report_data);
        setSelectedBank(res.data.report_data[0].account_no_1);
        setCurrentStep("Confirmation");
      }
    } catch (error) {
      console.log("Error fetching client account", error);
    }
  };

  const account = clientAccounts[0] || {};
  const banks = [];

  for (let i = 1; i <= 5; i++) {
    const bankName = account[`bank_name_${i}`]?.trim();
    const defaultFlag = account[`default_bank_flag_${i}`]?.trim();

    if (bankName) {
      banks.push({
        bankName,
        defaultFlag,
        accountNo: account[`account_no_${i}`],
      });
    }
  }

  const formattedOptions = banks.map((bank) => ({
    label: (
      <div className="bank-option">
        <div className="bank-name">{bank.bankName}</div>
        <div className="bank-account">{bank.accountNo}</div>
      </div>
    ),
    value: bank.accountNo, // or whole object if needed
  }));

  const defaultBank = clientAccounts.find((acc) => acc.default_flag === "Y");
  const defaultValue =
    defaultBank?.account_number ?? formattedOptions[0]?.value;

  const handleConfirm = async () => {
    setConfirmationLoading(true); // Start loading

    const transactionTemplate = {
      order_ref_number: "",
      scheme_code: "",
      trxn_type: "R",
      buy_sell_type: "",
      client_code: "",
      demat_physical: "P",
      order_amount: "",
      folio_no: "",
      remarks: "Order Remark",
      kyc_flag: "Y",
      sub_broker_code: "",
      euin_number: "",
      euin_declaration: "N",
      min_redemption_flag: "N",
      dpc_flag: "Y",
      all_units: "N",
      redemption_units: "",
      sub_broker_arn: "",
      bank_ref_no: "",
      account_no: "",
      mobile_no: "",
      email: "",
      mandate_id: "",
      filler1: "",
      trxn_order_id: "",
      trxn_status: "",
      trxn_remark: "",
    };

    let transactionDetailsArray = []; // Declare outside try block
    try {
      const res = await ServerRequest({
        url: "/mf/orderreferenceno",
        method: "get",
      });
      const orderRefNumber = res.data;

      // Filter out items that have no amount selected or invalid amounts BEFORE creating transactions
      const validTransferData = transferData.filter((item) => {
        const hasValidAmount =
          item.selectedAmount &&
          item.selectedAmount !== "" &&
          item.selectedAmount !== "-" &&
          (parseFloat(item.selectedAmount) > 0 ||
            item.selectedAmount === "__ALL_UNITS__");

        return hasValidAmount;
      });

      console.log(
        `Filtered ${validTransferData.length} valid items out of ${transferData.length} total items`,
      );

      transactionDetailsArray = validTransferData.map((item) => {
        // Fix: Check for "All Units" selection properly
        const isAllUnits =
          item.selectedAmount === "__ALL_UNITS__" ||
          item.selectedAmount === item.investment_value ||
          item.allUnitsSelected;

        const transactionObj = {
          ...transactionTemplate,
          order_ref_number: orderRefNumber, // ✅ same for all transactions in this order
          folio_no: item.folio_no || "",
          scheme_code:
            parseFloat(item.selectedAmount || 0) >
            parseFloat(item.sellInfo?.min_purchase_amount_l1)
              ? item.sellInfo?.scheme_code_l1
              : item.sellInfo?.scheme_code ||
                item.scheme_code ||
                item.Scheme_Code ||
                "",
          buy_sell_type: item.folio_no ? "ADDITIONAL" : "FRESH",
          client_code:
            customer?.account_code ||
            clientAccounts[0]?.client_code ||
            clientAccounts[0]?.account_code ||
            clientAccounts[0]?.primary_holder_pan ||
            "",
          account_no: selectedBank || "",
          order_amount: isAllUnits ? "" : String(item.selectedAmount ?? ""),
          all_units: isAllUnits ? "Y" : "N",
        };

        if (selectedBank?.bank_ref_no) {
          transactionObj.bank_ref_no = selectedBank.bank_ref_no;
        }

        return transactionObj;
      });

      console.log("🚀 Final transaction payload:", transactionDetailsArray);

      // Update transferData to only include valid items for UI display
      setTransferData(validTransferData);

      // Submit redemption order to backend
      const res2 = await ServerRequest({
        url: "/nse/redemption-order-entry",
        method: "post",
        data: {
          transaction_details: transactionDetailsArray,
          clientData: clientAccounts[0],
          transferData: validTransferData,
        },
      });

      const results = res2.data.transaction_details || [];

      const success = results.filter((r) => r.trxn_status === "TRXN SUCCESS");
      const fail = results.filter((r) => r.trxn_status === "TRXN FAILED");

      setSuccessData(success);
      setFailedData(fail);

      console.log(success, fail);

      if (success.length > 0) {
        setPurchaseMessage("Please check your email for transaction");
      }
      setCurrentStep("FinalConfirmation");
    } catch (error) {
      console.error("Failed to process redemption:", error);
      setConfirmMessage("Registration failed due to a server error.");
      setConfirmationType("fail");
      setCurrentStep("FinalConfirmation");
      console.log(transactionDetailsArray);
    } finally {
      setConfirmationLoading(false); // Stop loading
    }
  };

  const removeRow = (index) => {
    setTransferData((prev) => prev.filter((_, i) => i !== index));
  };

  const normalizeData = (data) => ({
    scheme_name: data.scheme_name || data.Scheme_Name || "",
    scheme_code: data.scheme_code || data.Scheme_Code || "",
    isin: data.isin || data.ISIN || "",
    folio_no: data.folio_no || "",
  });

  const handleClick = (symbol) => {
    if (!symbol) return;

    const normalized = normalizeData(symbol);
    const portfolioMatch = (portfolioData || []).find(
      (p) => (p.isin || p.ISIN) === normalized.isin,
    );

    const dataToPush = { ...(portfolioMatch || symbol), source: "search" };

    setTransferData((prev) => {
      const exists = prev.some(
        (item) =>
          (item.isin || item.ISIN) === (dataToPush.isin || dataToPush.ISIN),
      );
      return exists ? prev : [...prev, dataToPush];
    });
  };

  const handleAmountChange = (value, index) => {
    setTransferData((prev) =>
      prev.map((item, idx) => {
        if (idx === index) {
          // update only the clicked row
          const nav = parseFloat(item.current_nav || 0);
          const amount = parseFloat(value || 0);
          const investmentValue = parseFloat(item.investment_value || 0);

          const new_units = nav > 0 ? (amount / nav).toFixed(2) : "0.00";

          const post_transaction =
            item.type === "Buy"
              ? (investmentValue + amount).toFixed(2)
              : (investmentValue - amount).toFixed(2);

          return {
            ...item,
            selectedAmount: value,
            new_units,
            post_transaction,
          };
        }
        return item;
      }),
    );
  };

  return (
    <>
      {loading || confirmationLoading ? (
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            {/* <p className={opacityMsg ? 'fadeIn' : 'fadeOut'}>{loadingMsg}</p> */}
          </div>
        </div>
      ) : (
        <>
          <div className="swift-mf-buysell-popup-header">
            <div
              className={`swift-mf-popup-btn ${Array.isArray(popupData) ? popupData[0]?.type?.toLowerCase() : popupData?.type.toLowerCase() || ""}`}
            >
              Sell Transaction
            </div>
          </div>

          <div className="swift-mf-popup-body">
            <div
              className={`mf-BuySell-container ${currentStep === "Confirmation" ? "Confirmation" : ""}`}
            >
              {currentStep === "Process" && (
                <>
                  {DEVICE_SIZE === "S" ? (
                    <>
                      <BuySellCardContainer
                        transferData={transferData}
                        removeRow={removeRow}
                        handleAmountChange={handleAmountChange}
                        CustomSelect={CustomSelect}
                      />
                      <BuySellConfig
                        current_value={totalCurrentValue}
                        transaction_value={totalTransactionValue}
                        postTransaction={totalPostTransactionValue}
                      />
                    </>
                  ) : (
                    <>
                      <table>
                        <thead>
                          <tr>
                            <td className="scheme-name">Scheme Name</td>
                            <td className="post-transaction">Scheme Code</td>
                            <td className="isin">ISIN</td>
                            <td className="folio">Folio No</td>
                            <td className="">Units</td>
                            <td className="inv-value">Current Value</td>
                            <td>Current Nav</td>
                            <td className="post-transaction">
                              Transaction Value
                            </td>
                            <td className="">Units</td>
                            <td className="post-transaction">
                              Post Transaction Value
                            </td>
                            <td className="cross"></td>
                          </tr>
                        </thead>
                        <tbody>
                          {transferData.map((h, idx) => {
                            if (!h.sellInfo) return null;

                            const dropdownOptions =
                              generateRedemptionDropdownOptions(
                                parseInt(h.sellInfo.min_redemption_amount, 10),
                                parseInt(h.sellInfo.multiple, 10),
                                parseInt(
                                  h.sellInfo.redemption_allowed === "Y"
                                    ? h.investment_value
                                    : "",
                                  10,
                                ),
                              ).map((val) => ({
                                label:
                                  val === "ALL"
                                    ? "All Units"
                                    : val === ""
                                      ? "-"
                                      : priceFormat(val),
                                value:
                                  val === "ALL"
                                    ? h.investment_value
                                    : val === ""
                                      ? 0
                                      : val,
                              }));

                            return (
                              <tr key={idx}>
                                <td className="body-scheme-name">
                                  {h.scheme_name || h.Scheme_Name}
                                </td>
                                <td className="body-category">
                                  {parseFloat(h.selectedAmount || 0) >
                                  parseFloat(
                                    h.sellInfo?.min_redemption_amount_l1,
                                  )
                                    ? h.sellInfo?.scheme_code_l1
                                    : h.sellInfo?.scheme_code ||
                                      h.scheme_code ||
                                      h.Scheme_Code ||
                                      ""}
                                </td>
                                <td>{h.isin || h.ISIN}</td>
                                <td>{h.folio_no || ""}</td>
                                <td>{h.units || ""}</td>
                                <td>{priceFormat(h.investment_value || 0)}</td>
                                <td>{priceFormat(h.current_nav || 0)}</td>
                                <td className="custom-text-td">
                                  {h.sellInfo?.redemption_allowed === "Y" ? (
                                    <div className="bysell-custom-select-container">
                                      <CustomSelectPortal
                                        value={h.selectedAmount}
                                        defaultIndex={
                                          h.selectedAmount
                                            ? dropdownOptions.findIndex(
                                                (opt) =>
                                                  opt.value ===
                                                  h.selectedAmount,
                                              )
                                            : -1
                                        }
                                        options={dropdownOptions}
                                        width={180}
                                        height={50}
                                        placeholder="-"
                                        onTypeChange={(value) => {
                                          handleAmountChange(
                                            value ?? 0,
                                            idx,
                                          ); // <-- use row index
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <span className="not_allowed">
                                      Not Allowed
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {(h.investment_value || 0) < 100
                                    ? (numberFormat(h.units) ?? "-")
                                    : (numberFormat(h.new_units) ?? "-")}
                                </td>
                                <td>
                                  {h.post_transaction
                                    ? priceFormat(h.post_transaction)
                                    : "-"}
                                </td>
                                {h.source === "search" && (
                                  <td
                                    onClick={() => removeRow(idx)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <Cross color={COLOR_VARS["SWIFT_COLOR4"]} />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                          {transferData.some(
                            (val) => val.isSingle === false,
                          ) && (
                            <tr className="total-row">
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>{priceFormat(totalCurrentValue)}</td>
                              <td></td>
                              <td>{priceFormat(totalTransactionValue)}</td>
                              <td></td>
                              <td>{priceFormat(totalPostTransactionValue)}</td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
              {currentStep === "Confirmation" && (
                <div className="mf_buysell_bank_selection_container">
                  <p className="mf_buysell_bank_selection">Bank Details</p>
                  <CustomSelect
                    options={formattedOptions.map((opt) => opt.label)}
                    title={"Select Bank Details"}
                    defaultIndex={
                      selectedBank
                        ? formattedOptions.findIndex(
                            (opt) => opt.value === selectedBank,
                          )
                        : formattedOptions.findIndex(
                            (opt) => opt.value === defaultValue,
                          )
                    }
                    value={selectedBank || defaultValue}
                    onTypeChange={(label) => {
                      const selected = formattedOptions.find(
                        (opt) => opt.label === label,
                      );
                      setSelectedBank(selected?.value); // or entire selected object if needed
                    }}
                    width={200}
                    height={40}
                  />
                </div>
              )}

              <>
                {currentStep === "FinalConfirmation" && (
                  <>
                    {successData.length > 0 || failedData.length > 0 ? (
                      <ConfirmationTable
                        successData={successData}
                        failedData={failedData}
                        purchaseMessage={purchaseMessage}
                        transferData={transferData}
                      />
                    ) : (
                      <ConfirmationBox
                        msg={
                          confirmMessage === "Success"
                            ? "Your order has been successfully confirmed"
                            : "Looks like your transaction/order has failed."
                        }
                        addMsg={purchaseMessage}
                        confirmationType={confirmMessage}
                      />
                    )}
                  </>
                )}
              </>
            </div>
            {currentStep !== "FinalConfirmation" &&
              currentStep !== "TransactionResults" && (
                <div className="step-button mf-swift-buysell-confirm-button">
                  {currentStep === "Process" && (
                    <>
                      {/* <CustomIconButton
                                            title="Back"
                                            onClick={() => setPopup(false)}
                                            icon={<ArrowLeft2 size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />}
                                        /> */}
                      <button
                        className="btn-outline"
                        onClick={() => {
                          setPopup(false);
                        }}
                      >
                        Back
                      </button>
                      <CustomIconButton
                        title="Select bank"
                        onClick={handleProcess}
                        disabled={
                          !transferData.some(
                            (item) => Number(item.selectedAmount) > 0,
                          )
                        }
                        icon={null}
                      />
                    </>
                  )}
                  {currentStep === "Confirmation" && (
                    <>
                      <CustomIconButton
                        title="Back"
                        onClick={() => setCurrentStep("Process")}
                        icon={
                          <ArrowLeft2
                            size={20}
                            color={COLOR_VARS["SWIFT_COLOR6"]}
                          />
                        }
                      />
                      <CustomIconButton
                        title={`${currentStep}`}
                        onClick={handleConfirm}
                        icon={null}
                      />
                    </>
                  )}
                </div>
              )}
            {currentStep === "FinalConfirmation" && (
              <div class="step-button mf-swift-buysell-confirm-button">
                <CustomIconButton
                  title={`Close`}
                  onClick={() => setPopup(false)}
                  icon={<Cross size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

const BuySellCardContainer = ({
  transferData,
  handleAmountChange,
  CustomSelect,
}) => {
  return (
    <>
      {transferData.map((h, idx) => {
        const dropdownOptions = generateRedemptionDropdownOptions(
          parseInt(h.sellInfo?.min_redemption_amount, 10),
          parseInt(h.sellInfo?.multiple, 10),
          parseInt(
            h.sellInfo?.redemption_allowed === "Y" ? h.investment_value : "",
            10,
          ),
        ).map((val) => ({
          label:
            val === "ALL" ? "All Units" : val === "" ? "-" : priceFormat(val),
          value: val === "ALL" ? h.investment_value : val === "" ? 0 : val,
        }));

        return (
          <>
            <div className="mf-watchlist-card">
              <div className="mfwc-info">
                <div className="isin-price">
                  <p className="isin-type">{h.isin || h.ISIN}</p>
                  <p className="price-change">
                    {priceFormat(h.current_nav || 0)}
                  </p>
                </div>
                <p className="name">{h.scheme_name || h.Scheme_Name}</p>
                <p className="category">
                  {parseFloat(h.selectedAmount || 0) >
                  parseFloat(h.sellInfo?.min_purchase_amount_l1)
                    ? h.sellInfo?.scheme_code_l1
                    : h.sellInfo?.scheme_code ||
                      h.scheme_code ||
                      h.Scheme_Code ||
                      ""}
                </p>
              </div>
              <div className="mfwc-config">
                <div className="mfwc-config-row">
                  <p>
                    Investment Value
                    <span>{priceFormat(h.investment_value || 0)}</span>
                  </p>
                  <p>
                    Current Units<span>{numberFormat(h.units || "")}</span>
                  </p>
                </div>
                <div className="mfwc-config-row">
                  <p>
                    Transaction Value
                    {h.sellInfo?.redemption_allowed === "Y" ? (
                      <div className="bysell-custom-select-container">
                        <CustomSelect
                          value={h.selectedAmount}
                          defaultIndex={
                            h.selectedAmount
                              ? dropdownOptions.findIndex(
                                  (opt) => opt.value === h.selectedAmount,
                                )
                              : -1
                          }
                          options={dropdownOptions.map((opt) => opt.label)}
                          width={180}
                          height={50}
                          placeholder="-"
                          onTypeChange={(selectedLabel) => {
                            const matched = dropdownOptions.find(
                              (opt) => opt.label === selectedLabel,
                            );
                            handleAmountChange(matched?.value ?? 0, idx); // <-- use row index
                          }}
                        />
                      </div>
                    ) : (
                      <span className="not_allowed">Not Allowed</span>
                    )}
                  </p>
                  <p>
                    Units
                    <span>
                      {(h.investment_value || 0) < 3000
                        ? (numberFormat(h.units) ?? "-")
                        : (numberFormat(h.new_units) ?? "-")}
                    </span>
                  </p>
                </div>
                <div className="mfwc-config-row">
                  <p>
                    Post Transaction Value
                    <span>
                      {h.post_transaction
                        ? priceFormat(h.post_transaction)
                        : "-"}
                    </span>
                  </p>
                  <p></p>
                </div>
              </div>
            </div>
          </>
        );
      })}
    </>
  );
};

function BuySellConfig({
  current_value,
  transaction_value,
  all_units,
  postTransaction,
}) {
  return (
    <div className="portfolio-config">
      <div className="portfolio-rows">
        <div className="portfolio-info-box">
          <span>Current Value</span>
          <p>Rs. {numberFormat(current_value)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Transaction Value</span>
          <p>Rs. {numberFormat(transaction_value)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Post Transaction</span>
          <p>Rs. {numberFormat(postTransaction)}</p>
        </div>
      </div>
    </div>
  );
}

function TransactionResultsTable({
  successfulTransactions,
  failedTransactions,
}) {
  return (
    <div>
      <div className="transaction-results-card">
        {/* Header */}

        {/* Successful Transactions */}
        {successfulTransactions.length > 0 && (
          <div className={"swift-mf-popup-body"}>
            <h3 className="results-title">
              Successful Transactions ({successfulTransactions.length})
            </h3>
            <div className={"mf-BuySell-container"}>
              <div style={{ width: "50%" }}>
                <table>
                  <thead>
                    <tr>
                      <td className="scheme-name">Scheme Name</td>
                      <td>Order Amount</td>
                    </tr>
                  </thead>
                  <tbody>
                    {successfulTransactions.map((transaction, index) => (
                      <tr key={index}>
                        <td>{transaction.scheme_name || "N/A"}</td>
                        <td>
                          {numberFormat(
                            transaction.display_amount ||
                              transaction.order_amount ||
                              0,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Failed Transactions Table */}
        {failedTransactions.length > 0 && (
          <div className={"swift-mf-popup-body"}>
            <h3 className="results-title">
              Failed Transactions ({failedTransactions.length})
            </h3>

            <div className={"mf-BuySell-container"}>
              <div style={{ width: "50%" }}>
                <table>
                  <thead>
                    <tr>
                      <td>Scheme Name</td>
                      <td>Order Amount</td>
                      <td>Remark</td>
                    </tr>
                  </thead>
                  <tbody>
                    {failedTransactions.map((transaction, index) => (
                      <tr key={index}>
                        <td>{transaction.scheme_name || "N/A"}</td>
                        <td>
                          {numberFormat(
                            transaction.display_amount ||
                              transaction.order_amount ||
                              0,
                          )}
                        </td>
                        <td>{transaction.trxn_remark || "Unknown error"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MFSell;
